import { DocFieldState } from "../types";
import { DriverSection, SectionField } from "../../redux/onBoardingSlice.types";
import { DOC_STATE, SECTION_STATUS } from "./constants";
import { RejectionPayload, SectionTrackingState } from "./types";

/**
 * Builds the rejection payload from current review state.
 *
 * Pure function — takes a snapshot of state and returns data.
 * Easily unit-testable without any React context.
 *
 * Rules:
 * - If a section is rejected, all its fields are included under the section reason.
 * - Individual doc rejections are included only for fields not already covered by a section rejection.
 */
export const buildRejectionPayload = (
  sortedSections: DriverSection[],
  sectionStates: Record<string, SectionTrackingState>,
  allImageFields: Pick<SectionField, "fieldId" | "key">[],
  docStates: Record<string, DocFieldState>
): RejectionPayload => {
  const rejectedFields: { field: string; rejectionReason: string }[] = [];
  const sectionRejectedKeys = new Set<string>();

  sortedSections.forEach((section) => {
    const sState = sectionStates[section.sectionId];
    if (sState?.status === SECTION_STATUS.REJECTED && sState.confirmedReason) {
      section.fields.forEach((field) => {
        if (field.key) {
          rejectedFields.push({ field: field.key, rejectionReason: sState.confirmedReason });
          sectionRejectedKeys.add(field.key);
        }
      });
    }
  });

  allImageFields.forEach((field) => {
    if (field.key && sectionRejectedKeys.has(field.key)) return;
    const dState = docStates[field.fieldId];
    if (dState?.state === DOC_STATE.REJECTED && dState.reasons.length > 0 && field.key) {
      rejectedFields.push({ field: field.key, rejectionReason: dState.reasons.join(", ") });
    }
  });

  return { rejectedFields };
};
